'use client';

import { useEffect } from 'react';

/**
 * Consent Mode 를 지원하지 않는 트래커를 동의 이후에만 주입한다.
 *
 * <script type="text/plain" data-cookieconsent="..."> 를 JSX 로 렌더하면 안 된다.
 * Cookiebot 은 동의 시 그 스크립트를 "노드 교체"로 활성화하는데(이미 파싱된 스크립트는
 * type 만 바꿔서는 실행되지 않는다), 그 노드는 React 가 소유한 [locale] 레이아웃의
 * 자식이다. 이후 언어 전환으로 레이아웃이 언마운트되면 React 가 사라진 노드를
 * removeChild 하려다 NotFoundError 로 앱 전체가 죽는다.
 *
 * 그래서 document.head 에 직접 붙여 React 소유 밖에 둔다.
 */

const HOTJAR_SNIPPET = `
(function(h,o,t,j,a,r){
    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
    h._hjSettings={hjid:2047791,hjsv:6};
    a=o.getElementsByTagName('head')[0];
    r=o.createElement('script');r.async=1;
    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
    a.appendChild(r);
})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
`;

const SALESFORCE_MCAE_SNIPPET = `
piAId = '1085872';
piCId = '';
piHostname = 'go.3billion.io';
(function() {
  var s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = 'https://' + piHostname + '/pd.js';
  document.head.appendChild(s);
})();
`;

const CONSENT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

// 모듈 스코프다. 언어 전환 등으로 컴포넌트가 재마운트돼도 이미 head 에 남아 있는
// <script> 와 상태를 맞춰야 하므로 인스턴스별로 두면 안 된다.
const injected = new Set<string>();

const inject = (key: string, code: string): void => {
  if (injected.has(key)) return;
  injected.add(key);

  const element = document.createElement('script');
  element.textContent = code;
  document.head.appendChild(element);
};

export default function ConsentGatedScripts() {
  useEffect(() => {
    const syncWithConsent = () => {
      const consent = window.Cookiebot?.consent;
      if (consent?.statistics) inject('hotjar', HOTJAR_SNIPPET);
      if (consent?.marketing)
        inject('salesforce-mcae', SALESFORCE_MCAE_SNIPPET);
    };

    syncWithConsent();
    CONSENT_EVENTS.forEach(event =>
      window.addEventListener(event, syncWithConsent),
    );

    return () =>
      CONSENT_EVENTS.forEach(event =>
        window.removeEventListener(event, syncWithConsent),
      );
  }, []);

  return null;
}
